import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Baby, Building2, CircleHelp, ClipboardList, Route, School, Search, Siren, Users } from "lucide-react";
import api from "@/lib/api";
import { HELP_ARTICLES } from "@/lib/helpArticles";

const GROUP_META = {
  children: { label: "Children", icon: Baby },
  staff: { label: "Staff", icon: Users },
  schools: { label: "Schools", icon: School },
  councils: { label: "Local Authorities", icon: Building2 },
  programmes: { label: "Training Programmes", icon: Route },
  incidents: { label: "Incidents", icon: Siren },
};

export default function GlobalSearch({ autoFocus = false, onNavigate }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState({});
  const [help, setHelp] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setGroups({});
      setHelp([]);
      return;
    }
    const lower = q.toLowerCase();
    setHelp(
      HELP_ARTICLES.filter(
        (a) => a.title.toLowerCase().includes(lower) || a.keywords.includes(lower)
      ).slice(0, 5)
    );
    const t = setTimeout(() => {
      setLoading(true);
      api
        .get("/search", { params: { q } })
        .then((r) => setGroups(r.data.groups || {}))
        .catch(() => setGroups({}))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const go = (link) => {
    setOpen(false);
    setQuery("");
    if (onNavigate) onNavigate();
    navigate(link);
  };

  const hasResults = help.length > 0 || Object.keys(groups).length > 0;

  return (
    <div ref={boxRef} className="relative w-full max-w-xl" data-testid="global-search">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="search"
          role="combobox"
          aria-expanded={open}
          aria-label="Search help and records"
          data-testid="global-search-input"
          autoFocus={autoFocus}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
          placeholder="Search help, children, staff, schools…"
          className="h-10 w-full rounded-md border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm outline-none focus:border-primary focus:bg-white focus:ring-2 focus:ring-cyan-200"
        />
      </div>

      {open && query.trim().length >= 2 && (
        <div
          data-testid="global-search-results"
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-[70vh] overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg"
        >
          {help.length > 0 && (
            <div className="p-1.5">
              <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Help &amp; guidance</p>
              {help.map((a) => (
                <button
                  key={a.title}
                  type="button"
                  data-testid={`search-help-${a.title.slice(0, 24).replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                  onClick={() => go(a.link)}
                  className="flex w-full items-start gap-2.5 rounded px-2 py-2 text-left hover:bg-slate-50"
                >
                  <CircleHelp className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                  <span>
                    <span className="block text-sm font-medium text-slate-900">{a.title}</span>
                    <span className="block text-xs text-slate-500 line-clamp-2">{a.steps}</span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {Object.entries(groups).map(([key, items]) => {
            const meta = GROUP_META[key] || { label: key, icon: ClipboardList };
            return (
              <div key={key} className="border-t border-slate-100 p-1.5">
                <p className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{meta.label}</p>
                {items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    data-testid={`search-record-${key}-${item.id}`}
                    onClick={() => go(item.link)}
                    className="flex w-full items-center gap-2.5 rounded px-2 py-2 text-left hover:bg-slate-50"
                  >
                    <meta.icon className="h-4 w-4 shrink-0 text-slate-400" />
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-slate-900">{item.title}</span>
                      <span className="block truncate text-xs text-slate-500">{item.subtitle}</span>
                    </span>
                  </button>
                ))}
              </div>
            );
          })}

          {!hasResults && !loading && (
            <p className="px-4 py-3 text-sm text-slate-500" data-testid="global-search-empty">
              No results for “{query}”. Try “add child”, “report incident”, a Case ID, or a name.
            </p>
          )}
          {loading && <p className="px-4 py-3 text-sm text-slate-400" data-testid="global-search-loading">Searching…</p>}
        </div>
      )}
    </div>
  );
}
